import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import type { TestTypeCdphConfigInput } from '@alpha-edge/shared'
import { useApiEntity } from '../../hooks/use-api-entity'
import { useModal } from '../../hooks/use-modal'
import { api } from '../../services/api'
import {
  PageHeader,
  FormField,
  Alert,
  ConfirmDialog,
  btnPrimary,
  btnSecondary,
  btnDanger,
  card,
  colors,
  input,
} from '../../components/ui'

interface TestTypeForm {
  code: string
  name: string
  description: string
  category: string
  specimenType: string
  loincCode: string
  cptCode: string
  turnaroundHours: string
  // §7.6.25 Slice 4 -- temporary, non-destructive availability flag (reagent
  // stockout). Separate from the permanent catalog state (archive): an
  // unavailable test type still exists and stays ordered, but is honored by
  // the panel-completion gate so it can't hold a panel open (ADR 0012).
  temporarilyUnavailable: boolean
  // §7.6.23 Slice 4 -- CDPH SaPHIRE per-test-type reportability config.
  // Mirrors legacy `TestTypeModal.jsx`'s `setting.cdphConfig` fields.
  cdphConfig: TestTypeCdphConfigInput | null
}

const defaults: TestTypeForm = {
  code: '',
  name: '',
  description: '',
  category: '',
  specimenType: '',
  loincCode: '',
  cptCode: '',
  turnaroundHours: '',
  temporarilyUnavailable: false,
  cdphConfig: null,
}

export function TestTypeEditPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { data, isNew, loading, saving, error, success, save, cancel, update, setError } = useApiEntity<TestTypeForm>({
    path: '/test-types',
    listPath: '/test-types',
    defaultValue: defaults,
    // `cdphConfig` is `null` (never reportable) or a partial object in local
    // form state, but the update schema's field is `.optional()`, not
    // `.nullable()` -- map null to undefined so JSON.stringify drops the key
    // entirely rather than sending a literal `null` the schema would reject.
    transformPayload: (d) => ({ ...d, cdphConfig: d.cdphConfig ?? undefined }),
  })
  const archiveModal = useModal()
  const [archiving, setArchiving] = useState(false)

  const handleArchive = async () => {
    setArchiving(true)
    try {
      await api.delete(`/test-types/${id}`)
      navigate('/test-types')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to archive')
      archiveModal.close()
    } finally {
      setArchiving(false)
    }
  }

  if (loading) return <div style={{ color: colors.textMuted }}>Loading...</div>

  return (
    <div>
      <PageHeader
        title={isNew ? 'New Test Type' : `${data.code}: ${data.name}`}
        backTo="/settings/test-types"
        backLabel="Back to Test Types"
        actions={
          <>
            {!isNew && (
              <button onClick={archiveModal.open} style={btnDanger}>
                Archive
              </button>
            )}
            <button onClick={cancel} style={btnSecondary}>
              Cancel
            </button>
            <button onClick={save} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Saving...' : 'Save'}
            </button>
          </>
        }
      />
      {error && <Alert type="error" message={error} />}
      {success && <Alert type="success" message={success} />}

      <div style={{ ...card, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        <FormField
          name="code"
          label="Code"
          required
          value={data.code}
          onChange={(v) => update('code', v)}
          disabled={!isNew}
        />
        <FormField name="name" label="Name" required value={data.name} onChange={(v) => update('name', v)} />
        <FormField
          name="category"
          label="Category"
          value={data.category}
          onChange={(v) => update('category', v)}
          placeholder="e.g. infectious, chemistry, hematology"
        />
        <FormField
          name="specimenType"
          label="Specimen Type"
          value={data.specimenType}
          onChange={(v) => update('specimenType', v)}
          placeholder="e.g. nasopharyngeal swab, blood"
        />
        <FormField
          name="loincCode"
          label="LOINC Code"
          value={data.loincCode}
          onChange={(v) => update('loincCode', v)}
        />
        <FormField name="cptCode" label="CPT Code" value={data.cptCode} onChange={(v) => update('cptCode', v)} />
        <FormField
          name="turnaroundHours"
          label="Turnaround (hours)"
          value={data.turnaroundHours}
          onChange={(v) => update('turnaroundHours', v)}
        />
        <div style={{ gridColumn: '1 / -1' }}>
          <FormField
            name="description"
            label="Description"
            multiline
            rows={3}
            value={data.description}
            onChange={(v) => update('description', v)}
          />
        </div>
      </div>

      {/* §7.6.25 Slice 4 -- temporary availability toggle. Kept visually
          separate from the permanent catalog state (Archive, in the header)
          because it is a reversible reagent-stockout signal, not a retirement. */}
      <div style={{ ...card, marginTop: '1rem' }}>
        <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem', color: colors.textHeading }}>Availability</h3>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9375rem' }}>
          <input
            type="checkbox"
            checked={data.temporarilyUnavailable}
            onChange={(e) => update('temporarilyUnavailable', e.target.checked)}
            data-testid="temporarily-unavailable-checkbox"
          />
          Temporarily unavailable (reagent out of stock)
        </label>
        <p style={{ margin: '0.5rem 0 0', fontSize: '0.8125rem', color: colors.textMuted }}>
          When checked, this analyte is skipped by the completion gate in every panel that includes it, so an
          out-of-stock reagent can&rsquo;t hold a panel open. It stays orderable &mdash; this is temporary, not an
          archive.
        </p>
      </div>

      <div style={{ ...card, marginTop: '1rem' }}>
        <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem', color: colors.textHeading }}>CDPH Reporting</h3>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9375rem' }}>
          <input
            type="checkbox"
            checked={data.cdphConfig?.isReportable ?? false}
            onChange={(e) =>
              update('cdphConfig', {
                isReportable: e.target.checked,
                reportPolarity: data.cdphConfig?.reportPolarity ?? 'positive',
                loinc: data.cdphConfig?.loinc,
              })
            }
            data-testid="cdph-reportable-checkbox"
          />
          Report to CDPH
        </label>

        {data.cdphConfig?.isReportable && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '1rem',
              marginTop: '0.75rem',
              paddingTop: '0.75rem',
              borderTop: `1px solid ${colors.border}`,
            }}
          >
            <FormField name="cdph-report-polarity" label="Report Results">
              <select
                id="cdph-report-polarity"
                style={input}
                value={data.cdphConfig.reportPolarity}
                onChange={(e) =>
                  update('cdphConfig', {
                    ...(data.cdphConfig as TestTypeCdphConfigInput),
                    reportPolarity: e.target.value as TestTypeCdphConfigInput['reportPolarity'],
                  })
                }
                data-testid="cdph-report-polarity-select"
              >
                <option value="positive">Positive only</option>
                <option value="all">Positive &amp; negative</option>
              </select>
            </FormField>
            <FormField
              name="cdph-loinc"
              label="CDPH LOINC (optional override)"
              value={data.cdphConfig.loinc ?? ''}
              placeholder={data.loincCode || 'LOINC code'}
              onChange={(v) =>
                update('cdphConfig', {
                  ...(data.cdphConfig as TestTypeCdphConfigInput),
                  loinc: v || undefined,
                })
              }
            />
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={archiveModal.isOpen}
        onClose={archiveModal.close}
        onConfirm={handleArchive}
        title="Archive Test Type"
        message={`Are you sure you want to archive "${data.name}"? It will no longer appear in test selections.`}
        confirmLabel="Archive"
        loading={archiving}
      />
    </div>
  )
}
